import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';

export interface AnexoItem {
  id: number | string;
  nome: string;
  tamanho?: number;
  /** Quando true, o arquivo ainda não foi persistido no servidor */
  pending?: boolean;
  /** Quando true, o arquivo está em envio ativo para o servidor */
  uploading?: boolean;
}

const DEFAULT_EXTENSOES_ACEITAS = '.pdf,.doc,.docx,.txt,.html,.xls,.xlsx,.jpg,.jpeg,.png';
const DEFAULT_EXTENSOES_LABEL = 'PDF, DOC, DOCX, TXT, HTML, XLS, XLSX, JPG, JPEG, PNG.';
const DEFAULT_TAMANHO_MAX_MB = 40;

/**
 * Componente reutilizável de anexos.
 *
 * Responsável apenas pela apresentação e interação do usuário.
 * A lógica de API (upload, delete, download) é delegada ao componente pai via eventos.
 *
 * Uso básico:
 * ```html
 * <app-anexos
 *   [anexos]="listaAnexos"
 *   [carregando]="carregandoAnexos"
 *   [uploading]="uploadEmAndamento"
 *   [erro]="erroUpload"
 *   (fileSelected)="onFileSelected($event)"
 *   (remove)="onRemoverAnexo($event)"
 *   (download)="onDownloadAnexo($event)">
 * </app-anexos>
 * ```
 *
 * Para customizar extensões e tamanho aceitos:
 * ```html
 * <app-anexos
 *   [extensoesAceitas]="'.jpg,.png'"
 *   [extensoesLabel]="'JPG, PNG'"
 *   [tamanhoMaximoMb]="10"
 *   ...>
 * </app-anexos>
 * ```
 */
@Component({
  selector: 'app-anexos',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './anexos.component.html'
})
export class AnexosComponent implements OnChanges {
  /** Lista de anexos a exibir */
  @Input() anexos: AnexoItem[] = [];

  /** Exibe spinner de carregamento inicial */
  @Input() carregando = false;

  /** Exibe spinner no botão de upload enquanto um arquivo está sendo enviado */
  @Input() uploading = false;

  /** Permite selecionar múltiplos arquivos de uma vez */
  @Input() multiple = false;

  /** Mensagem de erro a exibir (null = sem erro) */
  @Input() erro: string | null = null;

  /** Extensões aceitas no input file (ex: '.pdf,.jpg') */
  @Input() extensoesAceitas = DEFAULT_EXTENSOES_ACEITAS;

  /** Texto descritivo das extensões permitidas */
  @Input() extensoesLabel = DEFAULT_EXTENSOES_LABEL;

  /** Tamanho máximo em MB */
  @Input() tamanhoMaximoMb = DEFAULT_TAMANHO_MAX_MB;

  /** Emitido quando o usuário seleciona um arquivo válido */
  @Output() fileSelected = new EventEmitter<File>();

  /** Emitido quando o usuário seleciona múltiplos arquivos válidos */
  @Output() filesSelected = new EventEmitter<File[]>();

  /** Emitido quando o usuário clica em remover (emite o id do anexo) */
  @Output() remove = new EventEmitter<number | string>();

  /** Emitido quando o usuário clica em baixar (emite o anexo completo) */
  @Output() download = new EventEmitter<AnexoItem>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  erroLocal: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['erro']) {
      this.erroLocal = this.erro;
    }
  }

  abrirSeletorArquivo(): void {
    this.fileInput?.nativeElement?.click();
  }

  onArquivoSelecionado(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = '';

    if (!files.length) {
      return;
    }

    const arquivosValidos = files.filter((file) => file.size <= this.tamanhoMaximoMb * 1024 * 1024);

    if (!arquivosValidos.length) {
      this.erroLocal = `O arquivo excede o tamanho máximo de ${this.tamanhoMaximoMb}MB.`;
      return;
    }

    this.erroLocal = arquivosValidos.length !== files.length
      ? `Alguns arquivos excedem o tamanho máximo de ${this.tamanhoMaximoMb}MB.`
      : null;

    if (this.multiple) {
      this.filesSelected.emit(arquivosValidos);
      return;
    }

    this.fileSelected.emit(arquivosValidos[0]);
  }

  getIconeAnexo(nome: string): 'file-text' | 'image' {
    const ext = nome.split('.').pop()?.toLowerCase() ?? '';
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext) ? 'image' : 'file-text';
  }
}
